import { useOverview } from '@/api/hooks';
import { useAgent } from '@/auth/use-agent';
import { formatCount, formatPercent } from '@/lib/format';
import { Card } from '@/ui/Card';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Loading } from '@/ui/Loading';
import { Notice } from '@/ui/Notice';
import { Stat } from '@/ui/Stat';
import { AgentsTable } from './AgentsTable';
import { RecentActivity } from './RecentActivity';
import { TournamentsTable } from './TournamentsTable';
import { tournamentLabel } from './tournament-label';

export function DashboardPage() {
  const { agent } = useAgent();
  const overview = useOverview();

  if (!agent.trackingEnabled) {
    return <Notice>Tracking is not enabled on this environment, so there are no results here.</Notice>;
  }
  if (overview.isPending) return <Loading what="dashboard" />;
  if (overview.error) return <ErrorNotice error={overview.error} />;

  const data = overview.data;
  const titles = new Map(data.tournaments.map((tournament) => [tournament.tournamentId, tournamentLabel(tournament)]));
  const running = data.tournaments.filter((tournament) => tournament.status === 'ACTIVE').length;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Dashboard</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat
          label="Tournaments"
          value={formatCount(data.tournaments.length)}
          hint={running ? `${formatCount(running)} running now` : 'None running now'}
        />
        <Stat label="Links issued" value={formatCount(data.totals.linksIssued)} />
        <Stat
          label="Players invited"
          value={formatCount(data.totals.playersInvited)}
          hint="Each player counted once, however many tournaments"
        />
        <Stat label="Players joined" value={formatCount(data.totals.playersRedeemed)} hint="Open-link joins included" />
        <Stat
          label="Join rate"
          value={formatPercent(data.totals.redeemRate)}
          hint="Share of invited players who joined; open-link joins are not in it"
        />
      </div>

      <Card
        title="Tournaments"
        description={
          agent.admin
            ? 'Every tournament with links, all agents. Open one for results per agent and the player list.'
            : 'Every tournament you issued links for. Open one for results and the player list.'
        }
      >
        <TournamentsTable tournaments={data.tournaments} />
      </Card>

      {agent.admin && (
        <Card title="Agents" description="Most players joined first. Click a column to sort by it.">
          <AgentsTable agents={data.agents} />
        </Card>
      )}

      <Card
        title="Recent activity"
        description={`The latest ${formatCount(data.recent.length)} events, newest first. ${formatCount(data.eventsCount)} recorded in total.`}
      >
        <RecentActivity events={data.recent} titles={titles} />
      </Card>
    </div>
  );
}
